import { create } from 'zustand';
import { NekoPosSdk } from 'neko-common';
import { configs } from '../configs';
import { authenticatedFetch } from '../utils/authenticatedFetch';

const sdk = new NekoPosSdk(configs.apiRoot);

interface Customer {
  id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  phone?: string;
}

interface CustomerState {
  customers: Customer[];
  isLoading: boolean;
  error: string | null;
  fetchCustomers: (searchValue?: string) => Promise<void>;
  checkinCustomer: (
    zaloId: string,
    otp: string
  ) => Promise<{
    success: boolean;
    customerId?: string;
    points?: number;
    hasMembership?: boolean;
    error?: string;
  }>;
  clearError: () => void;
}

export const useCustomerStore = create<CustomerState>((set, get) => ({
  customers: [],
  isLoading: false,
  error: null,

  fetchCustomers: async (searchValue?: string) => {
    try {
      set({ isLoading: true, error: null });

      // Call the actual API endpoint
      // Use smaller page size for better performance
      // When searching, use smaller size for faster results; when not searching, use moderate size
      const pageSize = searchValue ? '30' : '100';
      const params = new URLSearchParams({
        page: '1',
        size: pageSize,
      });

      if (searchValue) {
        params.append('q', searchValue);
      }

      const response = await authenticatedFetch(`/api/customers?${params}`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch customers: ${response.statusText}`);
      }

      const result = await response.json();

      // API returns array directly (via sendFormattedResponse), not wrapped in { data: ... }
      const customers = Array.isArray(result) ? result : result.data || [];

      console.log('📊 Customer Store - Fetched customers:', {
        customersCount: customers.length,
        isArray: Array.isArray(result),
        sampleCustomer: customers[0],
      });

      set({
        customers: customers,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error fetching customers:', error);
      set({
        error:
          error instanceof Error ? error.message : 'Failed to fetch customers',
        isLoading: false,
      });
    }
  },

  checkinCustomer: async (zaloId: string, otp: string) => {
    try {
      set({ isLoading: true, error: null });

      // First, get customer by Zalo ID
      const socialProfilesResponse = await authenticatedFetch(
        `/api/social_profiles?socialProfileId=${zaloId}&socialProfileType=zalo`,
        {
          method: 'GET',
        }
      );

      if (!socialProfilesResponse.ok) {
        throw new Error('Failed to fetch social profile');
      }

      const socialProfilesData = await socialProfilesResponse.json();
      const socialProfiles = socialProfilesData.data || [];

      if (socialProfiles.length === 0) {
        return {
          success: false,
          error: 'Customer not found',
          customerId: undefined,
          points: undefined,
          hasMembership: undefined,
        };
      }

      // Get customer details
      const customerResponse = await authenticatedFetch(
        `/api/customers/${socialProfiles[0].entityId}`,
        {
          method: 'GET',
        }
      );

      if (!customerResponse.ok) {
        throw new Error('Failed to fetch customer details');
      }

      const customer = await customerResponse.json();

      // Check if profile is incomplete
      if (!customer.firstName && !customer.lastName) {
        return {
          success: false,
          error: 'Profile incomplete',
          customerId: socialProfiles[0].entityId,
          points: undefined,
          hasMembership: undefined,
        };
      }

      // Proceed with check-in using SDK
      const response = await sdk.customerMembership.checkin({
        customerId: zaloId,
        otp,
      });

      if (!response.success) {
        return {
          success: false,
          error:
            typeof response.error === 'string'
              ? response.error
              : 'Check-in failed',
          customerId: undefined,
          points: undefined,
          hasMembership: undefined,
        };
      }

      set({ isLoading: false });

      return {
        success: true,
        customerId: socialProfiles[0].entityId,
        points: (response as any).data?.points || 0,
        hasMembership: (response as any).data?.hasMembership || false,
      };
    } catch (error) {
      console.error('Error during customer check-in:', error);
      set({
        error: error instanceof Error ? error.message : 'Check-in failed',
        isLoading: false,
      });

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Check-in failed',
        customerId: undefined,
        points: undefined,
        hasMembership: undefined,
      };
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));
